import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth, api } from '../context/AuthContext';
import { ShoppingCart, Check, AlertCircle, ArrowLeft, TrendingUp } from 'lucide-react';
import { Button } from '../components/ui/button';

const courseDetails = {
  'stock-market-101': {
    title: 'Stock Market 101',
    price: 499.00,
    description: 'Master the fundamentals of stock trading. Learn technical analysis, chart patterns, and risk management strategies.',
    features: ['Technical Analysis Basics', 'Chart Pattern Recognition', 'Risk Management', '4 Video Lessons']
  },
  'options-mastery': {
    title: 'Options Mastery',
    price: 599.00,
    description: 'Deep dive into options trading strategies. Learn calls, puts, spreads, and advanced hedging techniques.',
    features: ['Options Fundamentals', 'Calls & Puts Strategies', 'Spreads & Combinations', '4 Video Lessons']
  },
  'futures-pro': {
    title: 'Futures Pro',
    price: 999.99,
    description: 'Professional futures trading course. Master commodity futures, index futures, and leverage management.',
    features: ['Futures Market Overview', 'Commodity Trading', 'Leverage Management', '5 Video Lessons']
  },
  'live-trading-signals': {
    title: 'Live Trading Signals',
    price: 199.00,
    description: 'Get real-time trading signals from professional traders. Weekly subscription with daily alerts.',
    features: ['Daily Trade Alerts', 'Entry & Exit Points', 'Risk Levels', 'Weekly Subscription']
  }
};

export default function CheckoutPage() {
  const { courseId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const course = courseDetails[courseId];

  useEffect(() => {
    // Check URL for payment success
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    if (sessionId) {
      navigate(`/payment-success?session_id=${sessionId}`);
    }
  }, [navigate]);

  if (!course) {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-[#FF3333] mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Course Not Found</h1>
          <Link to="/#courses">
            <Button className="btn-primary mt-4">Browse Courses</Button>
          </Link>
        </div>
      </div>
    );
  }

  const handleCheckout = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await api.post(
        '/api/checkout/create-session',
        { 
          course_id: courseId,
          origin_url: window.location.origin
        }
      );
      
      // Redirect to Stripe Checkout
      window.location.href = response.data.url;
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to initiate checkout');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090B]">
      {/* Header */}
      <nav className="glass-header border-b border-[#27272A]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-[#007AFF] rounded-sm flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-white" />
            </div>
            <span className="font-bold text-xl tracking-tight text-white" style={{ fontFamily: 'Chivo, sans-serif' }}>
              COSSY NETWORK
            </span>
          </Link>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-6 py-12" data-testid="checkout-page">
        <Link to="/#courses" className="inline-flex items-center gap-2 text-[#71717A] hover:text-white mb-8">
          <ArrowLeft className="w-4 h-4" /> Back to Courses
        </Link>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Course Details */}
          <div className="bg-[#141417] border border-[#27272A] rounded-md p-6">
            <h1 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
              {course.title}
            </h1>
            <p className="text-[#A1A1AA] mb-6">{course.description}</p>
            
            <h3 className="text-white font-medium mb-3">What's Included:</h3>
            <ul className="space-y-2 mb-6">
              {course.features.map((feature) => (
                <li key={feature} className="flex items-center gap-2 text-[#A1A1AA]">
                  <Check className="w-4 h-4 text-[#00FF66]" />
                  {feature}
                </li>
              ))}
            </ul>

            <div className="pt-6 border-t border-[#27272A]">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[#71717A]">Price</span>
                <span className="text-2xl font-bold text-white font-mono">${course.price.toFixed(2)}</span>
              </div>
              {courseId === 'live-trading-signals' && (
                <p className="text-[#71717A] text-sm">Billed weekly</p>
              )}
            </div>
          </div>

          {/* Checkout Section */}
          <div className="bg-[#141417] border border-[#27272A] rounded-md p-6">
            <h2 className="text-xl font-bold text-white mb-6" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Complete Your Purchase
            </h2>

            <div className="bg-[#09090B] border border-[#27272A] rounded-md p-4 mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#007AFF] rounded-full flex items-center justify-center">
                  <span className="text-white font-bold">{user?.name?.charAt(0).toUpperCase()}</span>
                </div>
                <div>
                  <p className="text-white font-medium">{user?.name}</p>
                  <p className="text-[#71717A] text-sm">{user?.email}</p>
                </div>
              </div>
            </div>

            {error && (
              <div className="bg-[#FF3333]/10 border border-[#FF3333]/30 rounded-md p-4 mb-6 flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-[#FF3333]" />
                <p className="text-[#FF3333]">{error}</p>
              </div>
            )}

            <div className="space-y-4 mb-6">
              <div className="flex items-center justify-between py-2 border-b border-[#27272A]">
                <span className="text-[#A1A1AA]">{course.title}</span>
                <span className="text-white font-mono">${course.price.toFixed(2)}</span>
              </div>
              <div className="flex items-center justify-between py-2">
                <span className="text-white font-medium">Total</span>
                <span className="text-2xl font-bold text-white font-mono">${course.price.toFixed(2)}</span>
              </div>
            </div>

            <Button
              onClick={handleCheckout}
              className="btn-primary w-full"
              disabled={loading}
              data-testid="proceed-to-payment-btn"
            >
              <ShoppingCart className="w-4 h-4 mr-2" />
              {loading ? 'Processing...' : 'Proceed to Payment'}
            </Button>

            <p className="text-[#71717A] text-xs text-center mt-4">
              Secure payment powered by Stripe. Your payment information is encrypted and secure.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
